import { Link } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { getStorageUrl } from "@/lib/storage";

import { sectionShell } from "./landingContent";

export interface FeaturedCurriculum {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  audiences: string[];
}

interface FeaturedProgramsProps {
  curricula?: FeaturedCurriculum[];
  isLoading?: boolean;
}

/** Eligibility label for the tag pill — same rules the previous card used. */
function audienceLabel(audiences: string[]): string {
  const entrepreneur = audiences.includes("entrepreneur");
  const mentor = audiences.includes("mentor");
  if (entrepreneur && mentor) return "Everyone";
  if (entrepreneur) return "Entrepreneurs";
  if (mentor) return "Mentors";
  if (audiences.length === 0) return "Restricted";
  return "Staff";
}

function ProgramCard({ curriculum }: { curriculum: FeaturedCurriculum }) {
  const thumbnailUrl = getStorageUrl("course-content", curriculum.thumbnail_url);

  return (
    <Link
      to={`/curricula/${curriculum.id}`}
      className="group flex flex-col overflow-hidden rounded-[26px] bg-white transition-shadow duration-150 ease-out hover:shadow-eab-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eab-blue-dark focus-visible:ring-offset-2"
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          className="h-[190px] w-full shrink-0 object-cover"
          loading="lazy"
        />
      ) : (
        <div className="h-[190px] w-full shrink-0 bg-eab-surface" aria-hidden="true" />
      )}
      <div className="flex flex-1 flex-col gap-3 p-[26px]">
        <span className="self-start rounded-full bg-eab-tint px-[14px] py-1.5 text-[14px] font-semibold text-eab-green-dark">
          {audienceLabel(curriculum.audiences)}
        </span>
        <h3 className="text-pretty text-[25px] font-bold leading-[1.15] tracking-[-0.02em]">
          {curriculum.title}
        </h3>
        {curriculum.description && (
          <p className="text-pretty text-[16px] leading-[1.55] text-eab-neutral-700">
            {curriculum.description}
          </p>
        )}
        <span className="mt-1 text-[16px] font-semibold text-eab-blue-dark transition-colors group-hover:text-eab-blue">
          Take a look →
        </span>
      </div>
    </Link>
  );
}

function ProgramSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-[26px] bg-white">
      <Skeleton className="h-[190px] w-full rounded-none" />
      <div className="flex flex-col gap-3 p-[26px]">
        <Skeleton className="h-7 w-28 rounded-full" />
        <Skeleton className="h-7 w-full" />
        <Skeleton className="h-5 w-3/4" />
      </div>
    </div>
  );
}

/**
 * Featured programs: three admin-chosen curricula in a 3-up grid.
 *
 * Which three is controlled by curriculum sequence_order, which admins drag to
 * reorder in the admin Curriculum Reorder panel.
 */
export default function FeaturedPrograms({ curricula, isLoading }: FeaturedProgramsProps) {
  if (!isLoading && (!curricula || curricula.length === 0)) return null;

  return (
    <section className={`${sectionShell} pt-[76px]`}>
      <h2 className="mb-2.5 text-pretty text-[36px] font-extrabold tracking-[-0.03em] text-eab-neutral-900 lg:text-[54px]">
        Programs to get you moving
      </h2>
      <p className="mb-9 max-w-[600px] text-[19px] text-eab-neutral-700">
        Explore our curated learning paths designed to help you succeed
      </p>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? [0, 1, 2].map((i) => <ProgramSkeleton key={i} />)
          : curricula?.map((curriculum) => (
              <ProgramCard key={curriculum.id} curriculum={curriculum} />
            ))}
      </div>
    </section>
  );
}
